import { useState, type ReactNode } from 'react';
import { ButtonBase } from '@mui/material';

interface ExpandableChipListProps<T> {
  items: T[];
  renderChip: (item: T) => ReactNode;
  showMoreLabel: (hiddenCount: number) => string;
  showLessLabel: string;
  toggleTestId: string;
  maxVisible?: number;
}

// Keeps a chip row scannable at a glance — the rest is one click away via a "+N meer" toggle
// instead of always wrapping across several lines. `renderChip` must set its own `key` (it's
// rendered directly inside a `.map()`), which also lets each caller keep its own Chip styling.
export default function ExpandableChipList<T>({
  items,
  renderChip,
  showMoreLabel,
  showLessLabel,
  toggleTestId,
  maxVisible = 4,
}: ExpandableChipListProps<T>) {
  const [expanded, setExpanded] = useState(false);
  const hiddenCount = items.length - maxVisible;
  const visible = expanded ? items : items.slice(0, maxVisible);

  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {visible.map((item) => renderChip(item))}
      {hiddenCount > 0 && (
        <ButtonBase
          onClick={() => setExpanded((v) => !v)}
          className="rounded px-1 text-sm text-primary"
          data-testid={toggleTestId}
        >
          {expanded ? showLessLabel : showMoreLabel(hiddenCount)}
        </ButtonBase>
      )}
    </div>
  );
}
